import React, { useEffect, useState } from 'react';
import { Button } from '@tlx/atlas';
import { IS_LOGGED_IN_ALTINN_UNTIL } from '@Page/SalaryTaxCard/hooks/useSalaryTaxCardForReal';
import useSWR from 'swr';
import { fetcher, ResponseWrapper } from '@tlx/astro-shared';
import { TaxcardsOrderForm } from '@Page/SalaryTaxCard/component/Modals/OrderForm/TaxCardOrderForm';
import { AltinnData, AltinnNotification } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { AltinnLoginForm } from '@Page/SalaryTaxCard/component/Modals/LoginForm/AltinnLoginForm';
import { useTskContext } from '@Hooks/useTskContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { EmployeeInfo } from '@Page/EmployeeOverview/modals/taxcard/TaxcardModal';

interface FetchTaxcardPopupButtonProps {
    year: string;
    years: string[];
    altinn: AltinnData;
    allSelectedEmployees: number[];
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    className?: string;
}

export function FetchTaxcardPopupButton({
    year,
    years,
    altinn,
    allSelectedEmployees,
    open,
    setOpen,
    className,
}: FetchTaxcardPopupButtonProps) {
    const { data } = useSWR<ResponseWrapper<boolean>>(IS_LOGGED_IN_ALTINN_UNTIL);
    const isLoggedin = data ? data.value : false;

    const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo>({
        id: 0,
        nationalIdentityNumber: '',
        email: '',
        phoneNumber: '',
        countryCodeId: 161,
    });

    const tsk = useTskContext();

    const fields = useTaxcardNotification();

    useEffect(() => {
        async function fetchEmployee() {
            const url = `/v2/employee/${
                tsk.actualEmployeeId > 0 ? tsk.actualEmployeeId : tsk.loginEmployeeId
            }?fields=id,nationalIdentityNumber,email`;
            return fetcher<EmployeeInfo>(url);
        }

        fetchEmployee().then((res) => {
            setEmployeeInfo({
                id: res.id,
                nationalIdentityNumber: res.nationalIdentityNumber,
                email: res.email,
                phoneNumber: res.phoneNumber,
                countryCodeId: res.countryCodeId,
            });
        });
    }, []);

    return (
        <>
            <Button
                onClick={() => setOpen?.(true)}
                disabled={altinn.isWaitingForAltinn}
                className={className ?? ''}
                data-testid="fetch-taxcard-button"
            >
                <FormattedMessage id={'text_get_taxcards'} />
            </Button>
            {!isLoggedin && (
                <AltinnLoginForm altinn={altinn} open={open} setOpen={setOpen} employeeInfo={employeeInfo} />
            )}
            {isLoggedin && open && (
                <TaxcardsOrderForm
                    fields={fields}
                    year={year}
                    years={years}
                    open={open}
                    setOpen={setOpen}
                    altinn={altinn}
                    employeeInfo={employeeInfo}
                    setEmployeeInfo={setEmployeeInfo}
                    selectedEmployees={allSelectedEmployees}
                />
            )}
        </>
    );
}

interface Notification {
    id: number;
    value: string;
}

interface OrderInput<V> {
    selected: V;
    options: V[];
}

export interface Fields {
    notification: OrderInput<Notification>;
}

export function useTaxcardNotification() {
    const intl = useIntl();
    const notificationOne = {
        id: AltinnNotification.FIRST_CHANGE,
        value: intl.formatMessage({
            id: 'option_etaxdeductioncard_notification_first_change',
        }),
    };
    const notificationTwo = {
        id: AltinnNotification.NO_CHANGE,
        value: intl.formatMessage({
            id: 'option_etaxdeductioncard_notification_no_change',
        }),
    };

    return {
        notification: {
            selected: notificationOne,
            options: [notificationOne, notificationTwo],
        },
    };
}
